import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { getStudentQuotasSummary } from '../../../api/drmQuotasService';
import type { ManageLicenseListDto } from '../../../types/manageLicenses';

interface Props {
  license: ManageLicenseListDto;
  onClose: () => void;
}

function QuotaCard({ label, remaining }: { label: string; remaining: number }) {
  const { t } = useTranslation();
  return (
    <div className="border border-neutral-200 rounded p-3">
      <div className="text-xs uppercase tracking-wide text-neutral-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-neutral-900">{remaining}</div>
      <div className="text-xs text-neutral-500">{t('App.Admin.Licenses.LicenseDrawerQuotasTab.RemainingLabel')}</div>
    </div>
  );
}

export default function LicenseDrawerQuotasTab({ license, onClose: _onClose }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['license-quotas', license.studentId, license.bookId],
    queryFn: () => getStudentQuotasSummary(license.studentId, license.bookId),
    staleTime: 30_000,
    retry: false,
  });

  if (isLoading) return <div className="p-4 text-sm text-neutral-500">{t('App.Admin.Licenses.LicenseDrawerQuotasTab.Loading')}</div>;

  if (isError || !data) {
    return (
      <p className="p-4 text-sm text-neutral-500">
        {t('App.Admin.Licenses.LicenseDrawerQuotasTab.NoQuotas')}
      </p>
    );
  }

  return (
    <div className="p-4 grid grid-cols-3 gap-3">
      <QuotaCard label={t('App.Admin.Licenses.LicenseDrawerQuotasTab.PrintLabel')} remaining={data.print.remaining} />
      <QuotaCard label={t('App.Admin.Licenses.LicenseDrawerQuotasTab.CopyLabel')} remaining={data.copy.remaining} />
      <QuotaCard label={t('App.Admin.Licenses.LicenseDrawerQuotasTab.DownloadLabel')} remaining={data.download.remaining} />
    </div>
  );
}
